import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FilePenLine, Plus, Search, ChevronRight, ChevronLeft, Trash2, CreditCard as Edit2, Upload, Download, X, Users, Building2, Calendar, Phone, Mail, MapPin, AlertCircle, CheckCircle2, Clock, FileDown, UserRound, ShieldCheck, LayoutGrid, List, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useConventionsStore } from '../stores/conventionsStore';
import { useThemeStore } from '../stores/themeStore';
import { useUserStore } from '../stores/userStore';
import { auth } from '../config/firebase';
import { useAuthContext } from '../components/AuthProvider';
import { toast } from 'react-hot-toast';
import type { Convention, ConventionEmployee, ConventionInput, ConventionEmployeeInput } from '../types/convention';

const statutColors: Record<string, string> = {
  'En attente': 'bg-amber-100 text-amber-700',
  'En cours': 'bg-green-100 text-green-700',
  'Terminée': 'bg-gray-200 text-gray-600',
};

const empStatutColors: Record<string, string> = {
  'En recherche d\'emploi': 'bg-amber-100 text-amber-700',
  'Reclassement en CDI': 'bg-green-100 text-green-700',
  'CDD 12 mois': 'bg-emerald-100 text-emerald-700',
  'Process de recrutement': 'bg-blue-100 text-blue-700',
  'Création entreprise réalisée': 'bg-teal-100 text-teal-700',
  'Création entreprise en projet': 'bg-cyan-100 text-cyan-700',
  'Formations longues en cours': 'bg-indigo-100 text-indigo-700',
  'Formations longues en attente': 'bg-violet-100 text-violet-700',
  'Formations courtes en cours': 'bg-sky-100 text-sky-700',
  'Formations courtes en attentes': 'bg-slate-100 text-slate-700',
  'Formations courtes en attente de validation': 'bg-gray-100 text-gray-700',
  'Congé de maternité': 'bg-pink-100 text-pink-700',
  'En attente de retraite': 'bg-orange-100 text-orange-700',
  'Sortie': 'bg-red-100 text-red-700',
};

const emptyConvention: ConventionInput = {
  nom: '',
  entreprise_nom: '',
  entreprise_siret: '',
  entreprise_adresse: '',
  entreprise_contact_nom: '',
  entreprise_contact_email: '',
  entreprise_contact_telephone: '',
  comptable_nom: '',
  comptable_email: '',
  comptable_telephone: '',
  chef_de_projet: '',
  consultant_emails: [],
  date_debut: null,
  date_fin: null,
  statut: 'En attente',
  notes: '',
};

const emptyEmployee: ConventionEmployeeInput = {
  nom: '',
  prenom: '',
  email: '',
  telephone: '',
  telephone_portable: '',
  poste: '',
  statut: 'En recherche d\'emploi',
  date_entree: null,
  date_fin: null,
  consultant_attribue: '',
  notes: '',
  date_naissance: null,
  age: null,
  ville_dept: '',
};

function computeAge(dateNaissance: string | null, age: number | null = null): string {
  if (age !== null && age !== undefined) return `${age} ans`;
  if (!dateNaissance) return '—';
  const d = new Date(dateNaissance);
  if (Number.isNaN(d.getTime())) return '—';
  const today = new Date();
  let a = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) a--;
  return `${a} ans`;
}

function formatImportDate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
  }

  const text = String(value).trim();
  const frenchDate = text.match(/^(\d{1,2})[\/. -](\d{1,2})[\/. -](\d{4})$/);
  if (frenchDate) {
    return `${frenchDate[3]}-${frenchDate[2].padStart(2, '0')}-${frenchDate[1].padStart(2, '0')}`;
  }

  const isoDate = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoDate) {
    return `${isoDate[1]}-${isoDate[2].padStart(2, '0')}-${isoDate[3].padStart(2, '0')}`;
  }

  const numericDate = Number(text);
  if (Number.isFinite(numericDate)) {
    const parsed = XLSX.SSF.parse_date_code(numericDate);
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
  }

  return null;
}

function Conventions() {
  const { darkMode } = useThemeStore();
  const { user } = useAuthContext();
  const { isAdmin } = useUserStore();
  const {
    conventions, employees, loading,
    loadConventions, loadEmployees, createConvention, updateConvention, deleteConvention,
    addEmployee, updateEmployee, deleteEmployee, bulkAddEmployees, clearAllEmployees, syncAllToBeneficiaires
  } = useConventionsStore();

  const [search, setSearch] = useState('');
  const [selectedConvention, setSelectedConvention] = useState<Convention | null>(null);
  const [showConventionForm, setShowConventionForm] = useState(false);
  const [editingConvention, setEditingConvention] = useState<Convention | null>(null);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<ConventionEmployee | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Employee filters
  const [empFilterName, setEmpFilterName] = useState('');
  const [empFilterConsultant, setEmpFilterConsultant] = useState('');
  const [empFilterStatut, setEmpFilterStatut] = useState('');
  const [empFilterAgeMin, setEmpFilterAgeMin] = useState('');
  const [empFilterAgeMax, setEmpFilterAgeMax] = useState('');
  const [empFilterPeriodStart, setEmpFilterPeriodStart] = useState('');
  const [empFilterPeriodEnd, setEmpFilterPeriodEnd] = useState('');
  const [showEmpFilters, setShowEmpFilters] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadConventions();
  }, [user, loadConventions]);

  useEffect(() => {
    if (selectedConvention) {
      loadEmployees(selectedConvention.id);
    }
  }, [selectedConvention, loadEmployees]);

  const consultantsList = useMemo(() => {
    const set = new Set<string>();
    employees.forEach(e => { if (e.consultant_attribue) set.add(e.consultant_attribue); });
    return Array.from(set).sort();
  }, [employees]);

  const statutsList = useMemo(() => {
    const set = new Set<string>();
    employees.forEach(e => { if (e.statut) set.add(e.statut); });
    return Array.from(set);
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    return employees.filter(e => {
      if (empFilterName) {
        const q = empFilterName.toLowerCase().trim();
        const fullName = `${e.prenom} ${e.nom}`.toLowerCase();
        const reverseName = `${e.nom} ${e.prenom}`.toLowerCase();
        if (!fullName.includes(q) && !reverseName.includes(q) && !(e.nom || '').toLowerCase().includes(q) && !(e.prenom || '').toLowerCase().includes(q)) return false;
      }
      if (empFilterConsultant && e.consultant_attribue !== empFilterConsultant) return false;
      if (empFilterStatut && e.statut !== empFilterStatut) return false;
      const ageStr = computeAge(e.date_naissance, e.age);
      const ageNum = parseInt(ageStr, 10);
      if (!Number.isNaN(ageNum)) {
        if (empFilterAgeMin && ageNum < parseInt(empFilterAgeMin, 10)) return false;
        if (empFilterAgeMax && ageNum > parseInt(empFilterAgeMax, 10)) return false;
      } else {
        if (empFilterAgeMin || empFilterAgeMax) return false;
      }
      if (empFilterPeriodStart) {
        const filterStart = new Date(empFilterPeriodStart);
        const empStart = e.date_entree ? new Date(e.date_entree) : null;
        if (!empStart || empStart < filterStart) return false;
      }
      if (empFilterPeriodEnd) {
        const filterEnd = new Date(empFilterPeriodEnd);
        const empEnd = e.date_fin ? new Date(e.date_fin) : null;
        if (!empEnd || empEnd > filterEnd) return false;
      }
      return true;
    });
  }, [employees, empFilterName, empFilterConsultant, empFilterStatut, empFilterAgeMin, empFilterAgeMax, empFilterPeriodStart, empFilterPeriodEnd]);

  const hasEmpFilters = empFilterName || empFilterConsultant || empFilterStatut || empFilterAgeMin || empFilterAgeMax || empFilterPeriodStart || empFilterPeriodEnd;

  const resetEmpFilters = () => {
    setEmpFilterName('');
    setEmpFilterConsultant('');
    setEmpFilterStatut('');
    setEmpFilterAgeMin('');
    setEmpFilterAgeMax('');
    setEmpFilterPeriodStart('');
    setEmpFilterPeriodEnd('');
  };

  const filteredConventions = conventions.filter(c => {
    const s = search.toLowerCase();
    return c.nom.toLowerCase().includes(s) ||
      c.entreprise_nom.toLowerCase().includes(s) ||
      c.entreprise_siret.toLowerCase().includes(s);
  });

  const handleSaveConvention = async (data: ConventionInput) => {
    // Auto-add the current user's email to consultant_emails if not already present
    const currentUser = auth.currentUser;
    const userEmail = currentUser?.email?.toLowerCase() || '';
    const emails = [...(data.consultant_emails || [])];
    if (userEmail && !emails.some(e => e.toLowerCase() === userEmail)) {
      emails.push(userEmail);
    }
    const dataWithEmails = { ...data, consultant_emails: emails };

    if (editingConvention) {
      const ok = await updateConvention(editingConvention.id, dataWithEmails);
      if (ok) {
        setSelectedConvention(prev =>
          prev?.id === editingConvention.id ? { ...prev, ...dataWithEmails } as Convention : prev
        );
        toast.success('Convention mise à jour');
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } else {
      const created = await createConvention(dataWithEmails);
      if (created) {
        toast.success('Convention créée');
        setSelectedConvention(created);
      } else {
        toast.error('Erreur lors de la création');
      }
    }
    setShowConventionForm(false);
    setEditingConvention(null);
  };

  const handleDeleteConvention = async (id: string) => {
    if (!confirm('Supprimer cette convention et tous ses salariés ?')) return;
    await deleteConvention(id);
    if (selectedConvention?.id === id) setSelectedConvention(null);
    toast.success('Convention supprimée');
  };

  const handleSaveEmployee = async (data: ConventionEmployeeInput) => {
    if (!selectedConvention) return;
    if (editingEmployee) {
      await updateEmployee(editingEmployee.id, data);
      toast.success('Salarié mis à jour');
    } else {
      await addEmployee(selectedConvention.id, data);
      toast.success('Salarié ajouté');
    }
    setShowEmployeeForm(false);
    setEditingEmployee(null);
  };

  const handleDeleteEmployee = async (id: string) => {
    if (!confirm('Supprimer ce salarié ?')) return;
    await deleteEmployee(id);
    toast.success('Salarié supprimé');
  };

  const handleExport = () => {
    if (!selectedConvention) return;
    const rows = employees.map(e => ({
      'Nom': e.nom,
      'Prénom': e.prenom,
      'Tél': e.telephone,
      'Portable': e.telephone_portable,
      'Email': e.email,
      'Poste occupé': e.poste,
      'Âge': computeAge(e.date_naissance, e.age),
      'Ville / Dept': e.ville_dept || '',
      'Date de début': e.date_entree || '',
      'Date de fin': e.date_fin || '',
      'Consultant attribué': e.consultant_attribue,
      'Statut': e.statut,
      'Commentaires Commission': e.notes,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Salariés');
    XLSX.writeFile(wb, `convention_${selectedConvention.entreprise_nom || selectedConvention.nom}.xlsx`);
    toast.success('Export réussi');
  };

  const handleDownloadTemplate = () => {
    const template = [{
      'Nom': 'Dupont',
      'Prénom': 'Jean',
      'Tél': '0102030405',
      'Portable': '0601020304',
      'Email': 'jean.dupont@email.com',
      'Poste occupé': 'Opérateur de production',
      'Âge': '45',
      'Ville / Dept': 'Lyon / 69',
      'Date de début': '2026-01-15',
      'Date de fin': '2026-06-30',
      'Consultant attribué': 'Sophie Martin',
      'Statut': 'En recherche d\'emploi',
      'Commentaires Commission': '',
    }];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Salariés');
    XLSX.writeFile(wb, 'template_import_salaries.xlsx');
    toast.success('Template téléchargé');
  };

  const [importPreview, setImportPreview] = useState<ConventionEmployeeInput[]>([]);

  const handleFileSelected = async (file: File) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const normalizeHeader = (value: string) => value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
      const getValue = (row: Record<string, unknown>, ...names: string[]) => {
        const normalizedNames = names.map(normalizeHeader);
        const key = Object.keys(row).find(rowKey => normalizedNames.includes(normalizeHeader(rowKey)));
        return key ? row[key] : '';
      };
      const parseAge = (raw: unknown): number | null => {
        if (raw === '' || raw == null) return null;
        if (typeof raw === 'number') {
          return raw >= 18 && raw <= 100 ? raw : null;
        }
        if (raw instanceof Date) return null;
        const s = String(raw).trim();
        const m = s.match(/\d+/);
        if (!m) return null;
        const n = Number(m[0]);
        return n >= 18 && n <= 100 ? n : null;
      };
      const splitName = (full: string): { nom: string; prenom: string } => {
        const trimmed = full.trim().replace(/\s+/g, ' ');
        if (!trimmed) return { nom: '', prenom: '' };
        const parts = trimmed.split(' ');
        if (parts.length === 1) return { nom: parts[0], prenom: '' };
        return { nom: parts[0], prenom: parts.slice(1).join(' ') };
      };
      const employeesData: ConventionEmployeeInput[] = rows.map(r => {
        const rawNom = String(getValue(r, 'Nom', 'nom'));
        const rawPrenom = String(getValue(r, 'Prénom', 'prenom'));
        const rawTelephone = getValue(r, 'Tél', 'Téléphone', 'N°Tél.Fixe', 'telephone');
        const rawPortable = getValue(r, 'Portable', 'N°Tél.Mob.', 'telephone_portable');
        const rawEmail = String(getValue(r, 'Email', 'email'));
        const rawPoste = getValue(r, 'Poste occupé chez KANTAR', 'Poste occupé', 'Poste occupe', 'Poste actuel', 'Poste', 'poste_actuel', 'poste');
        const rawAge = getValue(r, 'Âge', 'Age', 'AGE', 'Age salarié', 'Âge salarié');
        const rawVille = String(getValue(r, 'Ville / Dept', 'Ville/Dept', 'Ville-Dept', 'Ville', 'Dept', 'ville_dept'));
        const allValues = Object.values(r);
        const importedAge = allValues
          .map(v => parseAge(v))
          .find(value => value !== null && value >= 18 && value <= 100) ?? null;
        const phoneValue = String(rawAge).replace(/\D/g, '');
        const shiftedAge = parseAge(rawTelephone);
        const hasShiftedAge = shiftedAge !== null && shiftedAge >= 18 && shiftedAge <= 100;
        const hasShiftedPhone = phoneValue.length >= 8;
        const isShiftedKantar = !rawVille
          && (hasShiftedPhone || (hasShiftedAge && typeof rawPortable === 'string' && rawPortable.trim() !== ''));

        const combinedName = String(getValue(r, 'CANDIDAT Nom Prénom', 'Candidat Nom Prénom', 'Candidat', 'Nom Prénom', 'Nom Prenom'));
        let nom = '';
        let prenom = '';
        if (combinedName) {
          ({ nom, prenom } = splitName(combinedName));
        } else if (isShiftedKantar) {
          ({ nom, prenom } = splitName(rawNom));
        } else {
          nom = rawNom;
          prenom = rawPrenom;
        }

        const posteActuel = String(isShiftedKantar ? rawPortable : rawPoste);
        return {
          nom,
          prenom: isShiftedKantar ? '' : prenom,
          telephone: isShiftedKantar ? String(rawAge) : String(rawTelephone),
          telephone_portable: isShiftedKantar ? '' : String(rawPortable),
          email: rawEmail.includes('@') ? rawEmail : '',
          poste: posteActuel,
          poste_actuel: posteActuel,
          statut: (getValue(r, 'Statut', 'statut') || 'En recherche d\'emploi') as ConventionEmployeeInput['statut'],
          date_entree: formatImportDate(getValue(r, 'Date de début', "Date d'entrée", 'Date début', 'date_entree', 'Début', 'Date début')),
          date_fin: formatImportDate(getValue(r, 'Date de fin', 'Date fin', 'date_fin', 'Fin')),
          consultant_attribue: String(getValue(r, 'Consultant attribué', 'Consultant', 'consultant attribué', 'consultant_attribue')),
          notes: String(getValue(r, 'Commentaires Commission', 'Notes', 'notes')),
          date_naissance: formatImportDate(getValue(r, 'Date de naissance', 'Date naissance', 'date_naissance')),
          age: importedAge,
          ville_dept: isShiftedKantar ? rawPrenom : rawVille,
        };
      }).filter(e => e.nom || e.prenom);
      if (employeesData.length === 0) {
        toast.error('Aucun salarié trouvé dans le fichier');
        return;
      }
      setImportPreview(employeesData);
    } catch (err) {
      toast.error('Erreur lors de la lecture du fichier');
    }
  };

  const [importing, setImporting] = useState(false);

  const handleConfirmImport = async () => {
    if (!selectedConvention || importPreview.length === 0 || importing) return;
    setImporting(true);
    try {
      const result = await bulkAddEmployees(selectedConvention.id, importPreview);
      const parts: string[] = [];
      if (result.created > 0) parts.push(`${result.created} nouveau${result.created > 1 ? 'x' : ''}`);
      if (result.updated > 0) parts.push(`${result.updated} mis à jour`);
      toast.success(parts.length > 0 ? `${parts.join(', ')}` : 'Aucun changement');
      setImportPreview([]);
      setShowImportModal(false);
    } catch (err: any) {
      console.error('Import error:', err);
      toast.error(err?.message || 'Erreur lors de l\'import des salariés');
    } finally {
      setImporting(false);
    }
  };

  const handleClearAllEmployees = async () => {
    if (!selectedConvention || employees.length === 0) return;
    if (!confirm(`Supprimer tous les ${employees.length} salariés de cette convention ? Cette action est irréversible.`)) return;
    try {
      await clearAllEmployees(selectedConvention.id);
      toast.success('Tous les salariés ont été supprimés');
    } catch (err) {
      console.error('Clear error:', err);
      toast.error('Erreur lors de la suppression des salariés');
    }
  };

  const [syncing, setSyncing] = useState(false);

  const handleSyncToBeneficiaires = async () => {
    if (!selectedConvention || employees.length === 0) return;
    setSyncing(true);
    try {
      const count = await syncAllToBeneficiaires(selectedConvention.id);
      toast.success(`${count} salariés synchronisés vers les candidats`);
    } catch (err) {
      console.error('Sync error:', err);
      toast.error('Erreur lors de la synchronisation vers les candidats');
    } finally {
      setSyncing(false);
    }
  };

  // ---- Convention Form Modal ----
  if (showConventionForm) {
    return (
      <ConventionFormModal
        darkMode={darkMode}
        initial={editingConvention}
        onCancel={() => { setShowConventionForm(false); setEditingConvention(null); }}
        onSave={handleSaveConvention}
      />
    );
  }

  // ---- Employee Form Modal ----
  if (showEmployeeForm && selectedConvention) {
    return (
      <EmployeeFormModal
        darkMode={darkMode}
        initial={editingEmployee}
        onCancel={() => { setShowEmployeeForm(false); setEditingEmployee(null); }}
        onSave={handleSaveEmployee}
      />
    );
  }

  // ---- Import Modal ----
  if (showImportModal && selectedConvention) {
    return (
      <ImportModal
        darkMode={darkMode}
        onCancel={() => { setShowImportModal(false); setImportPreview([]); }}
        onFileSelected={handleFileSelected}
        onConfirmImport={handleConfirmImport}
        onDownloadTemplate={handleDownloadTemplate}
        fileInputRef={fileInputRef}
        previewData={importPreview}
        setPreviewData={setImportPreview}
        importing={importing}
        consultantEmails={selectedConvention.consultant_emails || []}
      />
    );
  }

  // ---- Employee Detail View ----
  if (selectedConvention) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setSelectedConvention(null)}
          className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
        >
          <ChevronLeft className="w-4 h-4" />
          Retour aux conventions
        </button>

        <div className={`rounded-2xl overflow-hidden shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="bg-gradient-anthea p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <FilePenLine className="w-8 h-8 text-white" />
                <div>
                  <h1 className="text-2xl font-bold text-white">{selectedConvention.nom}</h1>
                  <p className="text-white/80 text-sm">{selectedConvention.entreprise_nom}</p>
                </div>
              </div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statutColors[selectedConvention.statut]} bg-white/90`}>
                {selectedConvention.statut}
              </span>
            </div>
          </div>

          <div className={`p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <InfoRow icon={Building2} label="SIRET" value={selectedConvention.entreprise_siret} darkMode={darkMode} />
            <InfoRow icon={MapPin} label="Adresse" value={selectedConvention.entreprise_adresse} darkMode={darkMode} />
            <InfoRow icon={Users} label="Contact" value={selectedConvention.entreprise_contact_nom} darkMode={darkMode} />
            <InfoRow icon={Mail} label="Email contact" value={selectedConvention.entreprise_contact_email} darkMode={darkMode} />
            <InfoRow icon={Phone} label="Téléphone contact" value={selectedConvention.entreprise_contact_telephone} darkMode={darkMode} />
            <InfoRow icon={UserRound} label="Interlocuteur Comptable" value={selectedConvention.comptable_nom} darkMode={darkMode} />
            <InfoRow icon={Mail} label="Email comptable" value={selectedConvention.comptable_email} darkMode={darkMode} />
            <InfoRow icon={Phone} label="Téléphone comptable" value={selectedConvention.comptable_telephone} darkMode={darkMode} />
            <InfoRow icon={UserRound} label="Chef de projet" value={selectedConvention.chef_de_projet} darkMode={darkMode} />
            <InfoRow icon={Calendar} label="Période" value={
              selectedConvention.date_debut || selectedConvention.date_fin
                ? `${selectedConvention.date_debut || '—'} → ${selectedConvention.date_fin || '—'}`
                : ''
            } darkMode={darkMode} />
          </div>

          {selectedConvention.consultant_emails && selectedConvention.consultant_emails.length > 0 && (
            <div className={`px-6 pb-4`}>
              <div className={`flex items-center gap-1.5 mb-2 text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <ShieldCheck className="w-4 h-4" />
                Consultants et chefs de projet autorisés
              </div>
              <div className="flex flex-wrap gap-1.5">
                {selectedConvention.consultant_emails.map((email, i) => (
                  <span key={i} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    {email}
                  </span>
                ))}
              </div>
            </div>
          )}

          {selectedConvention.notes && (
            <div className={`px-6 pb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p className="text-sm">{selectedConvention.notes}</p>
            </div>
          )}

          <div className={`px-6 pb-6 flex flex-wrap gap-2`}>
            <button
              onClick={() => { setEditingConvention(selectedConvention); setShowConventionForm(true); }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-anthea text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Edit2 className="w-4 h-4" />
              Modifier
            </button>
            <button
              onClick={() => handleDeleteConvention(selectedConvention.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
            >
              <Trash2 className="w-4 h-4" />
              Supprimer
            </button>
          </div>
        </div>

        {/* Employees section */}
        <div className={`rounded-2xl shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Salariés ({filteredEmployees.length}{filteredEmployees.length !== employees.length ? ` / ${employees.length}` : ''})
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setEditingEmployee(null); setShowEmployeeForm(true); }}
                className="flex items-center gap-2 px-3 py-2 bg-gradient-anthea text-white rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                <Upload className="w-4 h-4" />
                Importer
              </button>
              <button
                onClick={handleSyncToBeneficiaires}
                disabled={employees.length === 0 || syncing}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                title="Synchroniser les salariés vers les candidats"
              >
                {syncing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
                {syncing ? 'Sync...' : 'Sync Candidats'}
              </button>
              <button
                onClick={handleExport}
                disabled={employees.length === 0}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                <Download className="w-4 h-4" />
                Exporter
              </button>
              {isAdmin && (
              <button
                onClick={handleClearAllEmployees}
                disabled={employees.length === 0}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                title="Supprimer tous les salariés de cette convention"
              >
                <Trash2 className="w-4 h-4" />
                Tout supprimer
              </button>
              )}
            </div>
          </div>

          {/* Filters bar */}
          <div className={`px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => setShowEmpFilters(!showEmpFilters)}
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <Filter className="w-4 h-4" />
                Filtres
                {hasEmpFilters && <span className="inline-block w-2 h-2 rounded-full bg-anthea-blue" />}
              </button>
              {hasEmpFilters && (
                <button
                  onClick={resetEmpFilters}
                  className={`text-xs transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Réinitialiser
                </button>
              )}
            </div>
            {showEmpFilters && (
              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-3 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <div className="relative">
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Candidat (nom ou prénom)</label>
                  <Search className={`absolute left-2 top-7 w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    value={empFilterName}
                    onChange={e => setEmpFilterName(e.target.value)}
                    placeholder="Rechercher un candidat..."
                    className={`w-full pl-8 pr-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Consultant</label>
                  <select
                    value={empFilterConsultant}
                    onChange={e => setEmpFilterConsultant(e.target.value)}
                    className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                  >
                    <option value="">Tous</option>
                    {consultantsList.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Statut</label>
                  <select
                    value={empFilterStatut}
                    onChange={e => setEmpFilterStatut(e.target.value)}
                    className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                  >
                    <option value="">Tous</option>
                    {statutsList.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Âge min</label>
                    <input
                      type="number"
                      min="18"
                      max="100"
                      value={empFilterAgeMin}
                      onChange={e => setEmpFilterAgeMin(e.target.value)}
                      placeholder="18"
                      className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Âge max</label>
                    <input
                      type="number"
                      min="18"
                      max="100"
                      value={empFilterAgeMax}
                      onChange={e => setEmpFilterAgeMax(e.target.value)}
                      placeholder="100"
                      className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                    />
                  </div>
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Période début (à partir de)</label>
                  <input
                    type="date"
                    value={empFilterPeriodStart}
                    onChange={e => setEmpFilterPeriodStart(e.target.value)}
                    className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Période fin (jusqu'au)</label>
                  <input
                    type="date"
                    value={empFilterPeriodEnd}
                    onChange={e => setEmpFilterPeriodEnd(e.target.value)}
                    className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-xs uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <th className="py-3 px-4 font-semibold">Nom</th>
                  <th className="py-3 px-4 font-semibold">Poste</th>
                  <th className="py-3 px-4 font-semibold">Âge</th>
                  <th className="py-3 px-4 font-semibold">Ville / Dept</th>
                  <th className="py-3 px-4 font-semibold">Contact</th>
                  <th className="py-3 px-4 font-semibold">Consultant</th>
                  <th className="py-3 px-4 font-semibold">Statut</th>
                  <th className="py-3 px-4 font-semibold">Période</th>
                  <th className="py-3 px-4 font-semibold">Commentaires Commission</th>
                  <th className="py-3 px-4 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((e) => (
                  <tr key={e.id} className={`border-b ${darkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'} transition-colors`}>
                    <td className="py-3 px-4">
                      <div
                        onDoubleClick={() => { setEditingEmployee(e); setShowEmployeeForm(true); }}
                        title="Double-cliquer pour modifier le salarié"
                        className={`text-sm font-medium cursor-pointer ${darkMode ? 'text-white hover:text-blue-300' : 'text-gray-900 hover:text-blue-600'} transition-colors`}
                      >
                        {e.prenom} {e.nom}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{e.poste || '—'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{computeAge(e.date_naissance, e.age)}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{e.ville_dept || '—'}</span>
                    </td>
                    <td className="py-3 px-4">
                      {e.telephone_portable && <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{e.telephone_portable}</div>}
                      {e.telephone && <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{e.telephone}</div>}
                      <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{e.email || '—'}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{e.consultant_attribue || '—'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${empStatutColors[e.statut]}`}>{e.statut}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{e.date_entree ? new Date(e.date_entree).toLocaleDateString('fr-FR') : '—'}</span>
                      {e.date_fin && <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}> → {new Date(e.date_fin).toLocaleDateString('fr-FR')}</span>}
                    </td>
                    <td className="py-3 px-4 max-w-xs">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {e.notes || '—'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setEditingEmployee(e); setShowEmployeeForm(true); }}
                          className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEmployee(e.id)}
                          className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'hover:bg-red-900/50 text-red-400' : 'hover:bg-red-50 text-red-500'}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredEmployees.length === 0 && (
                  <tr>
                    <td colSpan={10} className={`py-8 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {employees.length === 0
                        ? 'Aucun salarié. Cliquez sur "Ajouter" ou "Importer".'
                        : 'Aucun salarié ne correspond aux filtres sélectionnés.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ---- Conventions List View ----
  return (
    <div className="space-y-6">
      <div className={`relative rounded-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="bg-gradient-anthea p-6">
          <div className="flex items-center gap-3">
            <FilePenLine className="w-8 h-8 text-white" />
            <div>
              <h1 className="text-2xl font-bold text-white">Conventions</h1>
              <p className="text-white/80 text-sm">Gestion des conventions de reclassement et des salariés à reclasser</p>
            </div>
          </div>
        </div>
      </div>

      <div className={`rounded-2xl shadow-sm p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Rechercher par nom, entreprise, SIRET..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-anthea-blue`}
            />
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-l-xl transition-colors ${viewMode === 'grid' ? 'bg-gradient-anthea text-white' : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                title="Vue grille"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-r-xl transition-colors ${viewMode === 'list' ? 'bg-gradient-anthea text-white' : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                title="Vue liste"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => { setEditingConvention(null); setShowConventionForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-anthea text-white rounded-xl hover:opacity-90 transition-opacity"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">Nouvelle convention</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anthea-blue"></div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredConventions.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedConvention(c)}
                className={`text-left p-4 rounded-xl border transition-all hover:shadow-md ${darkMode ? 'bg-gray-700 border-gray-600 hover:border-anthea-blue' : 'bg-white border-gray-200 hover:border-anthea-blue'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Building2 className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{c.entreprise_nom || c.nom}</h3>
                  </div>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statutColors[c.statut]}`}>{c.statut}</span>
                </div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`}>{c.nom}</p>
                {c.entreprise_siret && (
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>SIRET: {c.entreprise_siret}</p>
                )}
                <div className="flex items-center gap-1 mt-3">
                  <ChevronRight className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                  <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Voir les détails</span>
                </div>
              </button>
            ))}
            {filteredConventions.length === 0 && (
              <div className={`col-span-full py-12 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Aucune convention. Cliquez sur "Nouvelle convention" pour commencer.
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-xs uppercase ${darkMode ? 'text-gray-400 bg-gray-700/50' : 'text-gray-500 bg-gray-50'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <th className="py-3 px-4 font-semibold">Entreprise</th>
                  <th className="py-3 px-4 font-semibold">Convention</th>
                  <th className="py-3 px-4 font-semibold">SIRET</th>
                  <th className="py-3 px-4 font-semibold">Chef de projet</th>
                  <th className="py-3 px-4 font-semibold">Période</th>
                  <th className="py-3 px-4 font-semibold">Statut</th>
                  <th className="py-3 px-4 font-semibold w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filteredConventions.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedConvention(c)}
                    className={`border-b cursor-pointer transition-colors ${darkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{c.entreprise_nom || c.nom}</span>
                      </div>
                    </td>
                    <td className={`py-3 px-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.nom}</td>
                    <td className={`py-3 px-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.entreprise_siret || '—'}</td>
                    <td className={`py-3 px-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.chef_de_projet || '—'}</td>
                    <td className={`py-3 px-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {c.date_debut || c.date_fin ? `${c.date_debut || '—'} → ${c.date_fin || '—'}` : '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${statutColors[c.statut]}`}>{c.statut}</span>
                    </td>
                    <td className="py-3 px-4">
                      <ChevronRight className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    </td>
                  </tr>
                ))}
                {filteredConventions.length === 0 && (
                  <tr>
                    <td colSpan={7} className={`py-12 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Aucune convention. Cliquez sur "Nouvelle convention" pour commencer.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Info Row component ----
function InfoRow({ icon: Icon, label, value, darkMode }: { icon: any; label: string; value: string; darkMode: boolean }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <Icon className={`w-4 h-4 mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
      <div>
        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
        <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{value}</p>
      </div>
    </div>
  );
}

// ---- Convention Form Modal ----
function ConventionFormModal({
  darkMode, initial, onCancel, onSave
}: {
  darkMode: boolean;
  initial: Convention | null;
  onCancel: () => void;
  onSave: (data: ConventionInput) => void;
}) {
  const [form, setForm] = useState<ConventionInput>(initial ? {
    ...initial,
  } : emptyConvention);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  const inputClass = `w-full px-3 py-2 rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-anthea-blue`;
  const labelClass = `text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onCancel}>
      <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {initial ? 'Modifier la convention' : 'Nouvelle convention'}
            </h2>
            <button type="button" onClick={onCancel} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <div>
              <label className={labelClass}>Nom de la convention *</label>
              <input required value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} className={`${inputClass} mt-1`} placeholder="Ex: Convention de reclassement 2026" />
            </div>

            <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} pt-2`}>Entreprise</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Nom de l'entreprise *</label>
                <input required value={form.entreprise_nom} onChange={e => setForm({ ...form, entreprise_nom: e.target.value })} className={`${inputClass} mt-1`} placeholder="Ex: Tech Solutions SARL" />
              </div>
              <div>
                <label className={labelClass}>SIRET</label>
                <input value={form.entreprise_siret} onChange={e => setForm({ ...form, entreprise_siret: e.target.value })} className={`${inputClass} mt-1`} placeholder="123 456 789 00012" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Adresse</label>
              <input value={form.entreprise_adresse} onChange={e => setForm({ ...form, entreprise_adresse: e.target.value })} className={`${inputClass} mt-1`} placeholder="12 rue des Exemple, 75000 Paris" />
            </div>

            <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} pt-2`}>Contact</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Nom du contact</label>
                <input value={form.entreprise_contact_nom} onChange={e => setForm({ ...form, entreprise_contact_nom: e.target.value })} className={`${inputClass} mt-1`} placeholder="Jean Dupont" />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input type="email" value={form.entreprise_contact_email} onChange={e => setForm({ ...form, entreprise_contact_email: e.target.value })} className={`${inputClass} mt-1`} placeholder="contact@entreprise.fr" />
              </div>
              <div>
                <label className={labelClass}>Téléphone</label>
                <input value={form.entreprise_contact_telephone} onChange={e => setForm({ ...form, entreprise_contact_telephone: e.target.value })} className={`${inputClass} mt-1`} placeholder="01 23 45 67 89" />
              </div>
            </div>

            <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} pt-2`}>Interlocuteur Comptable</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Nom du contact</label>
                <input value={form.comptable_nom} onChange={e => setForm({ ...form, comptable_nom: e.target.value })} className={`${inputClass} mt-1`} placeholder="Jean Dupont" />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input type="email" value={form.comptable_email} onChange={e => setForm({ ...form, comptable_email: e.target.value })} className={`${inputClass} mt-1`} placeholder="comptable@entreprise.fr" />
              </div>
              <div>
                <label className={labelClass}>Téléphone</label>
                <input value={form.comptable_telephone} onChange={e => setForm({ ...form, comptable_telephone: e.target.value })} className={`${inputClass} mt-1`} placeholder="01 23 45 67 89" />
              </div>
            </div>

            <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} pt-2`}>Pilotage</div>
            <div>
              <label className={labelClass}>Chef de projet</label>
              <input value={form.chef_de_projet} onChange={e => setForm({ ...form, chef_de_projet: e.target.value })} className={`${inputClass} mt-1`} placeholder="Ex: Marie Durand" />
            </div>
            <div>
              <label className={labelClass}>Consultants / Chefs de projet autorisés</label>
              <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Saisissez les emails des consultants et chefs de projet qui doivent avoir accès à cette convention. Séparez par des virgules.</p>
              <input
                value={form.consultant_emails.join(', ')}
                onChange={e => setForm({ ...form, consultant_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                className={`${inputClass} mt-1`}
                placeholder="consultant1@email.fr, consultant2@email.fr"
              />
            </div>

            <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} pt-2`}>Période & Statut</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Date de début</label>
                <input type="date" value={form.date_debut || ''} onChange={e => setForm({ ...form, date_debut: e.target.value || null })} className={`${inputClass} mt-1`} />
              </div>
              <div>
                <label className={labelClass}>Date de fin</label>
                <input type="date" value={form.date_fin || ''} onChange={e => setForm({ ...form, date_fin: e.target.value || null })} className={`${inputClass} mt-1`} />
              </div>
              <div>
                <label className={labelClass}>Statut</label>
                <select value={form.statut} onChange={e => setForm({ ...form, statut: e.target.value as ConventionInput['statut'] })} className={`${inputClass} mt-1`}>
                  <option value="En attente">En attente</option>
                  <option value="En cours">En cours</option>
                  <option value="Terminée">Terminée</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>Commentaires Commission</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={`${inputClass} mt-1 min-h-[80px] resize-y`} placeholder="Commentaires Commission..." />
            </div>
          </div>

          <div className={`flex justify-end gap-2 p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button type="button" onClick={onCancel} className={`px-4 py-2 rounded-xl text-sm font-medium ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors`}>
              Annuler
            </button>
            <button type="submit" className="px-4 py-2 rounded-xl bg-gradient-anthea text-white text-sm font-medium hover:opacity-90 transition-opacity">
              {initial ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Employee Form Modal ----
function EmployeeFormModal({
  darkMode, initial, onCancel, onSave
}: {
  darkMode: boolean;
  initial: ConventionEmployee | null;
  onCancel: () => void;
  onSave: (data: ConventionEmployeeInput) => void;
}) {
  const [form, setForm] = useState<ConventionEmployeeInput>(initial ? {
    nom: initial.nom, prenom: initial.prenom, email: initial.email, telephone: initial.telephone,
    telephone_portable: initial.telephone_portable, poste: initial.poste, statut: initial.statut,
    date_entree: initial.date_entree, date_fin: initial.date_fin, consultant_attribue: initial.consultant_attribue,
    notes: initial.notes, date_naissance: initial.date_naissance, age: initial.age, ville_dept: initial.ville_dept
  } : emptyEmployee);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  const inputClass = `w-full px-3 py-2 rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-anthea-blue`;
  const labelClass = `text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onCancel}>
      <div className={`w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {initial ? 'Modifier le salarié' : 'Ajouter un salarié'}
            </h2>
            <button type="button" onClick={onCancel} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Prénom</label>
                <input value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} className={`${inputClass} mt-1`} />
              </div>
              <div>
                <label className={labelClass}>Nom *</label>
                <input required value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} className={`${inputClass} mt-1`} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Poste</label>
              <input value={form.poste} onChange={e => setForm({ ...form, poste: e.target.value })} className={`${inputClass} mt-1`} placeholder="Ex: Opérateur de production" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Date de naissance</label>
                <input type="date" value={form.date_naissance || ''} onChange={e => setForm({ ...form, date_naissance: e.target.value || null })} className={`${inputClass} mt-1`} />
              </div>
              <div>
                <label className={labelClass}>Ville / Dept</label>
                <input value={form.ville_dept} onChange={e => setForm({ ...form, ville_dept: e.target.value })} className={`${inputClass} mt-1`} placeholder="Ex: Lyon / 69" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Téléphone (fixe)</label>
                <input value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} className={`${inputClass} mt-1`} />
              </div>
              <div>
                <label className={labelClass}>Portable</label>
                <input value={form.telephone_portable} onChange={e => setForm({ ...form, telephone_portable: e.target.value })} className={`${inputClass} mt-1`} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={`${inputClass} mt-1`} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Statut</label>
                <select value={form.statut} onChange={e => setForm({ ...form, statut: e.target.value as ConventionEmployeeInput['statut'] })} className={`${inputClass} mt-1`}>
                  <option value="En recherche d'emploi">En recherche d'emploi</option>
                  <option value="Reclassement en CDI">Reclassement en CDI</option>
                  <option value="CDD 12 mois">CDD 12 mois</option>
                  <option value="Process de recrutement">Process de recrutement</option>
                  <option value="Création entreprise réalisée">Création entreprise réalisée</option>
                  <option value="Création entreprise en projet">Création entreprise en projet</option>
                  <option value="Formations longues en cours">Formations longues en cours</option>
                  <option value="Formations longues en attente">Formations longues en attente</option>
                  <option value="Formations courtes en cours">Formations courtes en cours</option>
                  <option value="Formations courtes en attentes">Formations courtes en attentes</option>
                  <option value="Formations courtes en attente de validation">Formations courtes en attente de validation</option>
                  <option value="Congé de maternité">Congé de maternité</option>
                  <option value="En attente de retraite">En attente de retraite</option>
                  <option value="Sortie">Sortie</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Consultant attribué</label>
                <input value={form.consultant_attribue} onChange={e => setForm({ ...form, consultant_attribue: e.target.value })} className={`${inputClass} mt-1`} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Date de début</label>
                <input type="date" value={form.date_entree || ''} onChange={e => setForm({ ...form, date_entree: e.target.value || null })} className={`${inputClass} mt-1`} />
              </div>
              <div>
                <label className={labelClass}>Date de fin</label>
                <input type="date" value={form.date_fin || ''} onChange={e => setForm({ ...form, date_fin: e.target.value || null })} className={`${inputClass} mt-1`} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Commentaires Commission</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={`${inputClass} mt-1 min-h-[60px] resize-y`} placeholder="Commentaires Commission..." />
            </div>
          </div>

          <div className={`flex justify-end gap-2 p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button type="button" onClick={onCancel} className={`px-4 py-2 rounded-xl text-sm font-medium ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors`}>
              Annuler
            </button>
            <button type="submit" className="px-4 py-2 rounded-xl bg-gradient-anthea text-white text-sm font-medium hover:opacity-90 transition-opacity">
              {initial ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Import Modal with Preview Table ----
function ImportModal({
  darkMode, onCancel, onFileSelected, onConfirmImport, onDownloadTemplate, fileInputRef,
  previewData, setPreviewData, importing, consultantEmails
}: {
  darkMode: boolean;
  onCancel: () => void;
  onFileSelected: (file: File) => void;
  onConfirmImport: () => void;
  onDownloadTemplate: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  previewData: ConventionEmployeeInput[];
  setPreviewData: React.Dispatch<React.SetStateAction<ConventionEmployeeInput[]>>;
  importing: boolean;
  consultantEmails: string[];
}) {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onFileSelected(file);
  };

  const updateRow = (index: number, field: keyof ConventionEmployeeInput, value: string) => {
    setPreviewData(prev => {
      const next = [...prev];
      const typedValue = field === 'age' ? (value === '' ? null : Number(value)) : value;
      next[index] = { ...next[index], [field]: typedValue };
      return next;
    });
  };

  const removeRow = (index: number) => {
    setPreviewData(prev => prev.filter((_, i) => i !== index));
  };

  const inputClass = `w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onCancel}>
      <div className={`w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Importer des salariés</h2>
          <button onClick={onCancel} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {previewData.length === 0 ? (
            <>
              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${dragOver ? 'border-anthea-blue bg-anthea-blue/5' : darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'}`}
              >
                <Upload className={`w-10 h-10 mx-auto mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Glissez un fichier Excel ici ou cliquez pour parcourir
                </p>
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Formats acceptés: .xlsx, .xls, .csv
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => { if (e.target.files?.[0]) onFileSelected(e.target.files[0]); }}
                />
              </div>

              {/* Column info */}
              <div className={`flex items-start gap-2 p-3 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                <AlertCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <p className="font-medium mb-1">Colonnes attendues:</p>
                  <p>Nom, Prénom, Tél, Portable, Email, Poste occupé, Âge, Ville / Dept, Date de début, Date de fin, Consultant attribué, Statut, Commentaires Commission</p>
                </div>
              </div>

              <button
                onClick={onDownloadTemplate}
                className={`flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-anthea-lime hover:text-anthea-lime/80' : 'text-anthea-blue hover:text-anthea-blue/80'} transition-colors`}
              >
                <FileDown className="w-4 h-4" />
                Télécharger un template Excel
              </button>
            </>
          ) : (
            <>
              {/* Preview table */}
              <div className="flex items-center justify-between">
                <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {previewData.length} salarié{previewData.length > 1 ? 's' : ''} à importer — vérifiez et modifiez avant validation
                </p>
                <button
                  onClick={() => setPreviewData([])}
                  className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                >
                  <X className="w-3 h-3" />
                  Annuler l'import
                </button>
              </div>

              <div className="overflow-x-auto rounded-xl border ${darkMode ? 'border-gray-700' : 'border-gray-200'}">
                <table className="w-full">
                  <thead>
                    <tr className={`text-left text-xs uppercase ${darkMode ? 'text-gray-400 bg-gray-700/50' : 'text-gray-500 bg-gray-50'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <th className="py-2 px-2 font-semibold w-8">#</th>
                      <th className="py-2 px-2 font-semibold">Prénom</th>
                      <th className="py-2 px-2 font-semibold">Nom</th>
                      <th className="py-2 px-2 font-semibold">Poste actuel</th>
                      <th className="py-2 px-2 font-semibold">Âge</th>
                      <th className="py-2 px-2 font-semibold">Ville / Dept</th>
                      <th className="py-2 px-2 font-semibold">Email</th>
                      <th className="py-2 px-2 font-semibold">Tél</th>
                      <th className="py-2 px-2 font-semibold">Portable</th>
                      <th className="py-2 px-2 font-semibold">Début</th>
                      <th className="py-2 px-2 font-semibold">Fin</th>
                      <th className="py-2 px-2 font-semibold">Consultant</th>
                      <th className="py-2 px-2 font-semibold">Statut</th>
                      <th className="py-2 px-2 font-semibold w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, i) => (
                      <tr key={i} className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <td className={`py-1.5 px-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{i + 1}</td>
                        <td className="py-1.5 px-2">
                          <input value={row.prenom} onChange={e => updateRow(i, 'prenom', e.target.value)} className={inputClass} placeholder="Prénom" />
                        </td>
                        <td className="py-1.5 px-2">
                          <input value={row.nom} onChange={e => updateRow(i, 'nom', e.target.value)} className={inputClass} placeholder="Nom" />
                        </td>
                        <td className="py-1.5 px-2">
                          <input value={row.poste_actuel ?? ''} onChange={e => updateRow(i, 'poste_actuel', e.target.value)} className={inputClass} placeholder="Poste actuel" />
                        </td>
                        <td className="py-1.5 px-2">
                          <input value={row.age != null ? String(row.age) : ''} onChange={e => updateRow(i, 'age', e.target.value)} className={inputClass} placeholder="Âge" />
                        </td>
                        <td className="py-1.5 px-2">
                          <input value={row.ville_dept} onChange={e => updateRow(i, 'ville_dept', e.target.value)} className={inputClass} placeholder="Ville / Dept" />
                        </td>
                        <td className="py-1.5 px-2">
                          <input value={row.email} onChange={e => updateRow(i, 'email', e.target.value)} className={inputClass} placeholder="Email" />
                        </td>
                        <td className="py-1.5 px-2">
                          <input value={row.telephone} onChange={e => updateRow(i, 'telephone', e.target.value)} className={inputClass} placeholder="Tél" />
                        </td>
                        <td className="py-1.5 px-2">
                          <input value={row.telephone_portable} onChange={e => updateRow(i, 'telephone_portable', e.target.value)} className={inputClass} placeholder="Portable" />
                        </td>
                        <td className="py-1.5 px-2">
                          <input value={row.date_entree || ''} onChange={e => updateRow(i, 'date_entree', e.target.value || null)} type="date" className={inputClass} />
                        </td>
                        <td className="py-1.5 px-2">
                          <input value={row.date_fin || ''} onChange={e => updateRow(i, 'date_fin', e.target.value || null)} type="date" className={inputClass} />
                        </td>
                        <td className="py-1.5 px-2">
                          <select
                            value={row.consultant_attribue}
                            onChange={e => updateRow(i, 'consultant_attribue', e.target.value)}
                            className={inputClass}
                          >
                            <option value="">—</option>
                            {Array.from(new Set([
                              ...consultantEmails,
                              ...previewData.map(item => item.consultant_attribue).filter(Boolean),
                            ])).map(consultant => (
                              <option key={consultant} value={consultant}>{consultant}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-1.5 px-2">
                          <select
                            value={row.statut}
                            onChange={e => updateRow(i, 'statut', e.target.value)}
                            className={inputClass}
                          >
                            <option value="En recherche d'emploi">En recherche d'emploi</option>
                            <option value="Reclassement en CDI">Reclassement en CDI</option>
                            <option value="CDD 12 mois">CDD 12 mois</option>
                            <option value="Process de recrutement">Process de recrutement</option>
                            <option value="Création entreprise réalisée">Création entreprise réalisée</option>
                            <option value="Création entreprise en projet">Création entreprise en projet</option>
                            <option value="Formations longues en cours">Formations longues en cours</option>
                            <option value="Formations longues en attente">Formations longues en attente</option>
                            <option value="Formations courtes en cours">Formations courtes en cours</option>
                            <option value="Formations courtes en attentes">Formations courtes en attentes</option>
                            <option value="Formations courtes en attente de validation">Formations courtes en attente de validation</option>
                            <option value="Congé de maternité">Congé de maternité</option>
                            <option value="En attente de retraite">En attente de retraite</option>
                            <option value="Sortie">Sortie</option>
                          </select>
                        </td>
                        <td className="py-1.5 px-2">
                          <button
                            onClick={() => removeRow(i)}
                            className={`p-1 rounded-lg transition-colors ${darkMode ? 'hover:bg-red-900/50 text-red-400' : 'hover:bg-red-50 text-red-500'}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={`flex items-start gap-2 p-3 rounded-xl text-xs ${darkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-500" />
                <p>Vous pouvez modifier chaque champ directement dans le tableau. Cliquez sur "Importer" pour ajouter ces {previewData.length} salarié{previewData.length > 1 ? 's' : ''} à la convention.</p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-between items-center gap-2 p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex gap-2">
            {previewData.length > 0 && (
              <button
                onClick={onDownloadTemplate}
                className={`flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
              >
                <FileDown className="w-4 h-4" />
                Template
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className={`px-4 py-2 rounded-xl text-sm font-medium ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors`}>
              Fermer
            </button>
            {previewData.length > 0 && (
              <button
                onClick={onConfirmImport}
                disabled={importing}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-anthea text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {importing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {importing ? 'Import...' : `Importer (${previewData.length})`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Conventions;